import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import api from '../../api/api';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { fetchProducts } from '../../store/slices/productSlice';
import { InventoryAdjustment } from '../../types';
import ItemQuantityField from '../../components/ItemQuantityField';
import './InventoryResortOpsPage.css';
import { getSelectableProductSearchItems } from '../../utils/selectableProducts';
import { escapeHtml, getInventoryPrintStyles, getWeightTypeLabel } from '../../utils/inventoryPrint';
import { openAndPrintHtml } from '../../utils/printDocument';
import InventoryWarehouseSelect from '../../components/InventoryWarehouseSelect';

type ResortRow = { key: number; fromProductId: string; fromQuantity: string; toProductId: string; toQuantity: string };
type ResortMetaItem = { fromProductId: number; fromQuantity: number; toProductId: number; toQuantity: number };
const PRINT_STYLE_STORAGE_KEY = 'crm4chef.resort.print-style.v1';
const clamp = (value: number, min: number, max: number) => Math.max(min, Math.min(max, value));
const loadPrintStyle = (): { fontSize: number; cellPadding: number } => {
  try {
    const raw = JSON.parse(localStorage.getItem(PRINT_STYLE_STORAGE_KEY) || '{}');
    return {
      fontSize: clamp(Number(raw?.fontSize) || 12, 10, 20),
      cellPadding: clamp(Number(raw?.cellPadding) || 6, 2, 16),
    };
  } catch {
    return { fontSize: 12, cellPadding: 6 };
  }
};
const savePrintStyle = (fontSize: number, cellPadding: number) =>
  localStorage.setItem(PRINT_STYLE_STORAGE_KEY, JSON.stringify({ fontSize, cellPadding }));

const normalizeResortMetaItem = (item: any): ResortMetaItem => ({
  fromProductId: item?.FromProductId ?? item?.fromProductId ?? 0,
  fromQuantity: item?.FromQuantity ?? item?.fromQuantity ?? 0,
  toProductId: item?.ToProductId ?? item?.toProductId ?? 0,
  toQuantity: item?.ToQuantity ?? item?.toQuantity ?? 0,
});

const thStyle: React.CSSProperties = { padding: '8px', textAlign: 'left', background: 'var(--crm-surface-alt)', color: 'var(--crm-text)', borderBottom: '2px solid var(--crm-border)' };
const tdStyle: React.CSSProperties = { padding: '6px 8px', color: 'var(--crm-text)', borderBottom: '1px solid var(--crm-table-row-border)' };
const btn: React.CSSProperties = { padding: '8px 12px', border: '1px solid var(--crm-info)', borderRadius: 'var(--crm-radius-md)', cursor: 'pointer', background: 'var(--crm-info-bg)', color: 'var(--crm-info)' };
const successButton: React.CSSProperties = { ...btn, borderColor: 'var(--crm-success)', background: 'var(--crm-success-bg)', color: 'var(--crm-success)' };
const dangerButton: React.CSSProperties = { ...btn, borderColor: 'var(--crm-danger)', background: 'var(--crm-danger-bg)', color: 'var(--crm-danger)' };
const neutralButton: React.CSSProperties = { ...btn, borderColor: 'var(--crm-border)', background: 'var(--crm-surface-alt)', color: 'var(--crm-text)' };

const InventoryResortOpsPage: React.FC = () => {
  const dispatch = useAppDispatch();
  const { products } = useAppSelector((s) => s.product);
  const [items, setItems] = useState<InventoryAdjustment[]>([]);
  const [selected, setSelected] = useState<InventoryAdjustment | null>(null);
  const [selectedResortIds, setSelectedResortIds] = useState<number[]>([]);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);
  const initialPrintStyle = React.useMemo(() => loadPrintStyle(), []);
  const [printFontSize, setPrintFontSize] = useState(initialPrintStyle.fontSize);
  const [printCellPadding, setPrintCellPadding] = useState(initialPrintStyle.cellPadding);

  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [warehouseId, setWarehouseId] = useState('');
  const [notes, setNotes] = useState('');
  const nextRowKey = useRef(1);
  const emptyRow = (): ResortRow => ({ key: nextRowKey.current++, fromProductId: '', fromQuantity: '', toProductId: '', toQuantity: '' });
  const [rows, setRows] = useState<ResortRow[]>([{ key: 0, fromProductId: '', fromQuantity: '', toProductId: '', toQuantity: '' }]);
  const [saveError, setSaveError] = useState('');
  const [saving, setSaving] = useState(false);
  const productSearchItems = React.useMemo(
    () => getSelectableProductSearchItems(
      products,
      rows.flatMap((row) => [row.fromProductId, row.toProductId])
    ),
    [products, rows]
  );

  const resetForm = () => {
    setDate(new Date().toISOString().slice(0, 10));
    setWarehouseId('');
    setNotes('');
    setRows([emptyRow()]);
    setSaveError('');
    setEditingId(null);
  };

  useEffect(() => {
    savePrintStyle(printFontSize, printCellPadding);
  }, [printFontSize, printCellPadding]);

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get<InventoryAdjustment[]>('/inventoryadjustments', { params: { operationType: 'Resort' } });
      setItems(res.data);
      setSelectedResortIds((prev) => prev.filter((id) => res.data.some((row) => row.id === id)));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    dispatch(fetchProducts());
    load();
  }, [dispatch]);

  const parseResortMetaRows = (reason: string): ResortMetaItem[] => {
    const reasonParts = (reason || '').split('|');
    if (reasonParts.length < 2) return [];

    try {
      const parsed = JSON.parse(reasonParts[1]) as any[];
      if (!Array.isArray(parsed)) return [];

      return parsed
        .map(normalizeResortMetaItem)
        .filter((row) => row.fromProductId > 0 && row.toProductId > 0 && row.fromQuantity > 0 && row.toQuantity > 0);
    } catch {
      return [];
    }
  };

  const open = async (id: number) => {
    const res = await api.get<InventoryAdjustment>(`/inventoryadjustments/${id}`);
    setSelected(res.data);
  };

  const startEdit = (item: InventoryAdjustment) => {
    setEditingId(item.id);
    setDate(item.date.slice(0, 10));
    setWarehouseId(item.warehouseId ? String(item.warehouseId) : '');
    setNotes(item.notes || '');
    let mapped: ResortRow[] = parseResortMetaRows(item.reason || '').map((m) => ({
      key: nextRowKey.current++,
      fromProductId: String(m.fromProductId),
      fromQuantity: String(m.fromQuantity),
      toProductId: String(m.toProductId),
      toQuantity: String(m.toQuantity),
    }));
    if (!mapped.length) mapped = [emptyRow()];
    setRows(mapped);
    setShowForm(true);
    setSaveError('');
  };

  const updateRow = (idx: number, patch: Partial<ResortRow>) => setRows((prev) => prev.map((r, i) => (i === idx ? { ...r, ...patch } : r)));
  const addRow = (afterIndex?: number) => {
    const row = emptyRow();
    setRows(prev => afterIndex == null ? [...prev, row] : [...prev.slice(0, afterIndex + 1), row, ...prev.slice(afterIndex + 1)]);
  };
  const removeRow = (idx: number) => setRows((prev) => prev.filter((_, i) => i !== idx));

  const save = async (e: React.FormEvent) => {
    e.preventDefault();
    if (saving) return;
    setSaveError('');
    if (!rows.length || rows.some(row => !row.fromProductId || !row.toProductId ||
      !Number.isFinite(Number(row.fromQuantity)) || Number(row.fromQuantity) <= 0 ||
      !Number.isFinite(Number(row.toQuantity)) || Number(row.toQuantity) <= 0)) {
      setSaveError('Для кожної позиції оберіть обидва продукти та вкажіть додатну кількість.'); return;
    }
    const payload = {
      date: new Date(date).toISOString(),
      warehouseId: warehouseId ? Number(warehouseId) : null,
      notes,
      items: rows
        .map((r) => ({
          fromProductId: Number(r.fromProductId),
          fromQuantity: Number(r.fromQuantity),
          toProductId: Number(r.toProductId),
          toQuantity: Number(r.toQuantity),
        })),
    };
    setSaving(true);
    try {
      if (editingId) await api.put(`/inventoryadjustments/${editingId}/resort`, payload);
      else await api.post('/inventoryadjustments/resort', payload);
      setShowForm(false);
      resetForm();
      await load();
      if (selected && editingId) await open(editingId);
    } catch (error) {
      const data = axios.isAxiosError<{ message?: string; title?: string; errors?: Record<string, string[]> } | string>(error) ? error.response?.data : undefined;
      setSaveError((typeof data === 'string' ? data : data?.message || (data?.errors && Object.values(data.errors).flat().join(' ')) || data?.title) ||
        'Не вдалося зберегти або оновити дані пересорту. Перевірте з’єднання та оновіть список перед повтором.');
    } finally { setSaving(false); }
  };

  const remove = async (id: number) => {
    if (!window.confirm('Скасувати пересорт? Він залишиться в історії, але більше не впливатиме на склад.')) return;
    const reason = window.prompt('Вкажіть причину скасування:');
    if (!reason?.trim()) return;
    await api.post(`/inventoryadjustments/${id}/cancel`, { reason: reason.trim() });
    if (selected?.id === id) setSelected(null);
    setSelectedResortIds((prev) => prev.filter((x) => x !== id));
    await load();
  };

  const toggleResortSelection = (id: number) => {
    setSelectedResortIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  const toggleAllVisibleResorts = () => {
    const visibleIds = items.map((x) => x.id);
    const allSelected = visibleIds.length > 0 && visibleIds.every((id) => selectedResortIds.includes(id));
    if (allSelected) {
      setSelectedResortIds((prev) => prev.filter((id) => !visibleIds.includes(id)));
      return;
    }
    setSelectedResortIds((prev) => Array.from(new Set([...prev, ...visibleIds])));
  };

  const markSelectedResortsAsAccounting = async () => {
    if (selectedResortIds.length === 0) {
      alert('Оберіть хоча б один пересорт.');
      return;
    }
    await api.put('/inventoryadjustments/accounting-transfer', {
      ids: selectedResortIds,
      isTransferredToAccounting: true,
    });
    await load();
    if (selected && selectedResortIds.includes(selected.id)) await open(selected.id);
  };

  const print = () => {
    if (!selected) return;
    const getWeightByProductId = (productId: number) => getWeightTypeLabel(products.find((p) => p.id === productId)?.weightType || '');
    const details = parseResortMetaRows(selected.reason || '')
      .map((row) => `
        <tr>
          <td>${escapeHtml(products.find((p) => p.id === row.fromProductId)?.name || '—')}</td>
          <td style="text-align:right;">${row.fromQuantity.toFixed(3)}</td>
          <td style="text-align:center;">${escapeHtml(getWeightByProductId(row.fromProductId))}</td>
          <td>${escapeHtml(products.find((p) => p.id === row.toProductId)?.name || '—')}</td>
          <td style="text-align:right;">${row.toQuantity.toFixed(3)}</td>
          <td style="text-align:center;">${escapeHtml(getWeightByProductId(row.toProductId))}</td>
        </tr>`)
      .join('');
    void openAndPrintHtml(`
      <html><head><title>Пересорт #${selected.id}</title>
      <style>body{font-family:Arial,sans-serif;padding:20px;color:#2c3e50}${getInventoryPrintStyles()} .pdf-receipt-root th, .pdf-receipt-root td { font-size:${printFontSize}px !important; padding:${printCellPadding}px ${printCellPadding + 2}px !important; } .pdf-receipt-root table{table-layout:fixed;} .pdf-receipt-root td{word-break:break-word;overflow-wrap:anywhere;}</style>
      </head><body>
      <div class="pdf-receipt-root">
        <section class="receipt-sheet">
          <div class="receipt-top">
            <div class="receipt-title-row">
              <h2>Пересорт #${selected.id}</h2>
              <span class="page-index">Сторінка 1 / 1</span>
            </div>
            <div class="receipt-meta-grid">
              <div><strong>Компанія:</strong> Весільний Зал</div>
              <div><strong>Дата:</strong> ${new Date(selected.date).toLocaleDateString('uk-UA')}</div>
              <div><strong>Операція:</strong> Пересорт складу</div>
              <div><strong>Примітка:</strong> ${escapeHtml(selected.notes || '—')}</div>
            </div>
          </div>
          <table>
            <thead><tr><th>Продукт (мінус)</th><th style="text-align:right;">К-сть</th><th style="text-align:center;">Од.</th><th>Продукт (плюс)</th><th style="text-align:right;">К-сть</th><th style="text-align:center;">Од.</th></tr></thead>
            <tbody>${details || '<tr><td colspan="6" style="text-align:center;color:#7f8c8d;">Позиції відсутні</td></tr>'}</tbody>
          </table>
          <div class="receipt-bottom">
            <div>Передав(ла): ____________________</div>
            <div>Прийняв(ла): ____________________</div>
          </div>
        </section>
      </div>
      </body></html>`, { windowFeatures: 'width=1000,height=700' });
  };

  const printSelectedResortsSheet = async () => {
    if (selectedResortIds.length === 0) {
      alert('Оберіть хоча б один пересорт для друку.');
      return;
    }
    const docsRaw = await Promise.all(
      selectedResortIds.map(async (id) => {
        try {
          const res = await api.get<InventoryAdjustment>(`/inventoryadjustments/${id}`);
          return res.data;
        } catch {
          return null;
        }
      })
    );
    const docs = docsRaw.filter((x): x is InventoryAdjustment => x != null);
    if (docs.length === 0) {
      alert('Не вдалося завантажити обрані пересорти для друку.');
      return;
    }
    const sortedDocs = [...docs].sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
    const periodFrom = new Date(sortedDocs[0].date).toLocaleDateString('uk-UA');
    const periodTo = new Date(sortedDocs[sortedDocs.length - 1].date).toLocaleDateString('uk-UA');
    const rows = sortedDocs.flatMap((doc) => {
      const metaRows = parseResortMetaRows(doc.reason || '');
      return metaRows.map((row) => ({
        date: new Date(doc.date).toLocaleDateString('uk-UA'),
        fromProductName: products.find((p) => p.id === row.fromProductId)?.name || '—',
        fromUnit: getWeightTypeLabel(products.find((p) => p.id === row.fromProductId)?.weightType || ''),
        fromQuantity: row.fromQuantity,
        toProductName: products.find((p) => p.id === row.toProductId)?.name || '—',
        toUnit: getWeightTypeLabel(products.find((p) => p.id === row.toProductId)?.weightType || ''),
        toQuantity: row.toQuantity,
      }));
    });
    const rowsHtml = rows.map((row) => `
      <tr>
        <td>${escapeHtml(row.date)}</td>
        <td>${escapeHtml(row.fromProductName)}</td>
        <td style="text-align:center;">${escapeHtml(row.fromUnit)}</td>
        <td style="text-align:right;">${row.fromQuantity.toFixed(3)}</td>
        <td>${escapeHtml(row.toProductName)}</td>
        <td style="text-align:center;">${escapeHtml(row.toUnit)}</td>
        <td style="text-align:right;">${row.toQuantity.toFixed(3)}</td>
      </tr>`).join('');

    void openAndPrintHtml(`<html><head><title>Пересорт за період</title>
      <style>
        @page { size: A4 landscape; margin: 10mm; }
        body{font-family:Arial,sans-serif;padding:14px;color:#2c3e50}
        ${getInventoryPrintStyles()}
        .pdf-receipt-root table { table-layout: fixed; width: 100%; }
        .pdf-receipt-root th, .pdf-receipt-root td { font-size:${printFontSize}px !important; padding:${printCellPadding}px ${printCellPadding + 2}px !important; vertical-align: top; }
        .pdf-receipt-root td { word-break: break-word; overflow-wrap: anywhere; }
        .pdf-receipt-root .col-date { width: 11%; white-space: nowrap; }
        .pdf-receipt-root .col-prod { width: 24%; }
        .pdf-receipt-root .col-unit { width: 7%; text-align:center; white-space: nowrap; }
        .pdf-receipt-root .col-qty { width: 10%; text-align:right; white-space: nowrap; }
      </style>
      </head><body>
      <div class="pdf-receipt-root"><section class="receipt-sheet">
        <div class="receipt-top">
          <div class="receipt-title-row">
            <h2>Пересорт за період з ${periodFrom} по ${periodTo}</h2>
            <span class="page-index">Обрано документів: ${sortedDocs.length}</span>
          </div>
        </div>
        <table>
          <thead><tr>
            <th class="col-date">Дата</th>
            <th class="col-prod">Продукт (мінус)</th>
            <th class="col-unit">Од.</th>
            <th class="col-qty">Вага</th>
            <th class="col-prod">Продукт (плюс)</th>
            <th class="col-unit">Од.</th>
            <th class="col-qty">Вага</th>
          </tr></thead>
          <tbody>${rowsHtml || '<tr><td colspan="7" style="text-align:center;color:#7f8c8d;">Позиції відсутні</td></tr>'}</tbody>
        </table>
      </section></div>
      </body></html>`, { windowFeatures: 'width=1200,height=800' });
  };

  const selectedMetaRows = selected ? parseResortMetaRows(selected.reason || '') : [];
  const productNameById = new Map(products.map((p) => [p.id, p.name]));

  return (
    <div className="page-shell page-shell--wide inventory-resort-page" style={{ maxWidth: '1500px' }}>
      <h2 style={{ color: 'var(--crm-text)', borderBottom: '2px solid var(--crm-accent)', paddingBottom: '8px' }}>Пересорт</h2>
      {saveError && <p role="alert" className="inventory-resort-form__error">{saveError}</p>}
      <div className="no-print responsive-split-layout" style={{ marginTop: '16px' }}>
        <div style={{ flex: '0 0 420px' }}>
          <div style={{ marginBottom: '12px' }}>
            <button onClick={() => { resetForm(); setShowForm(true); }} style={{ ...successButton, width: '100%' }}>+ Новий пересорт</button>
          </div>
          <div style={{ marginBottom: '12px' }}>
            <button
              type="button"
              disabled={selectedResortIds.length === 0}
              onClick={printSelectedResortsSheet}
              style={{ ...(selectedResortIds.length === 0 ? neutralButton : btn), width: '100%' }}
            >
              🖨 Друк обраних ({selectedResortIds.length})
            </button>
          </div>
          <div style={{ marginBottom: '12px' }}>
            <button
              type="button"
              disabled={selectedResortIds.length === 0}
              onClick={markSelectedResortsAsAccounting}
              style={{ ...(selectedResortIds.length === 0 ? neutralButton : successButton), width: '100%' }}
            >
              ✓ Передані в бухгалтерію ({selectedResortIds.length})
            </button>
          </div>
          <div style={{ marginBottom: '12px', background: 'var(--crm-surface-alt)', border: '1px solid var(--crm-border)', borderRadius: 'var(--crm-radius-md)', padding: '10px' }}>
            <div style={{ fontWeight: 600, fontSize: '13px', color: 'var(--crm-text)', marginBottom: '8px' }}>Налаштування друку</div>
            <div className="inventory-print-settings-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr auto', gap: '8px', alignItems: 'end' }}>
              <label style={{ fontSize: '12px', color: 'var(--crm-text)' }}>
                Шрифт (px)
                <input type="number" min="10" max="20" step="1" value={printFontSize} onChange={(e) => setPrintFontSize(clamp(Number(e.target.value) || 12, 10, 20))} style={{ width: '100%', marginTop: '4px', padding: '4px 6px', borderRadius: '6px', border: '1px solid var(--crm-border)', background: 'var(--crm-input-bg)', color: 'var(--crm-text)' }} />
              </label>
              <label style={{ fontSize: '12px', color: 'var(--crm-text)' }}>
                Відступ (px)
                <input type="number" min="2" max="16" step="1" value={printCellPadding} onChange={(e) => setPrintCellPadding(clamp(Number(e.target.value) || 6, 2, 16))} style={{ width: '100%', marginTop: '4px', padding: '4px 6px', borderRadius: '6px', border: '1px solid var(--crm-border)', background: 'var(--crm-input-bg)', color: 'var(--crm-text)' }} />
              </label>
              <button type="button" onClick={() => { setPrintFontSize(12); setPrintCellPadding(6); }} style={{ ...neutralButton, padding: '6px 10px', fontSize: '12px' }}>Скинути</button>
            </div>
          </div>
          <div className="crm-mobile-table-scroll" style={{ maxHeight: '620px', overflowY: 'auto', background: 'var(--crm-surface)', border: '1px solid var(--crm-border)', borderRadius: 'var(--crm-radius-md)', boxShadow: 'var(--crm-shadow-sm)' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead><tr><th style={{ ...thStyle, width: '32px', textAlign: 'center' }}><input type="checkbox" checked={items.length > 0 && items.every((x) => selectedResortIds.includes(x.id))} onChange={toggleAllVisibleResorts} title="Обрати всі" /></th><th style={thStyle}>Дата</th><th style={thStyle}>Бух.</th><th style={thStyle}>Позицій</th><th style={thStyle}>Σ Δ</th></tr></thead>
              <tbody>
                {items.map((i) => (
                  <tr key={i.id} style={{ cursor: 'pointer', background: selected?.id === i.id ? 'var(--crm-info-bg)' : undefined }} onClick={() => open(i.id)}>
                    <td style={{ ...tdStyle, textAlign: 'center' }} onClick={(e) => e.stopPropagation()}><input type="checkbox" checked={selectedResortIds.includes(i.id)} onChange={() => toggleResortSelection(i.id)} title={`Обрати пересорт #${i.id}`} /></td>
                    <td style={tdStyle}>{new Date(i.date).toLocaleDateString('uk-UA')}</td>
                    <td style={{ ...tdStyle, textAlign: 'center', color: i.isTransferredToAccounting ? 'var(--crm-success)' : 'var(--crm-text-muted)', fontWeight: 700 }}>{i.isTransferredToAccounting ? '✓' : '—'}</td>
                    <td style={tdStyle}>{i.itemCount}</td>
                    <td style={tdStyle}>{i.totalDelta.toFixed(3)}</td>
                  </tr>
                ))}
                {items.length === 0 && <tr><td colSpan={5} style={{ ...tdStyle, padding: '16px', textAlign: 'center', color: 'var(--crm-text-muted)' }}>{loading ? 'Завантаження...' : 'Даних немає'}</td></tr>}
              </tbody>
            </table>
          </div>
        </div>
        <div className="inventory-resort-content" style={{ flex: 1 }}>
          {showForm && (
            <form onSubmit={save} className="inventory-resort-form">
              <h3>{editingId ? 'Редагування пересорту' : 'Новий пересорт'}</h3>
              <div className="inventory-resort-form__meta">
                <label>Дата<input type="date" required disabled={saving} value={date} onChange={(e) => setDate(e.target.value)} /></label>
                <InventoryWarehouseSelect value={warehouseId} onChange={setWarehouseId} disabled={saving} />
                <label>Примітка<input disabled={saving} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Коментар до документа" /></label>
              </div>
              <p className="inventory-resort-form__hint">Оберіть, що списати зі складу та що оприбуткувати замість нього. Кількості можуть відрізнятися; одиниця показана біля кожного продукту.</p>
              <div className="inventory-resort-rows crm-position-list">
                {rows.map((r, idx) => (
                  <section className="inventory-resort-row crm-position-card" key={r.key} aria-label={`Позиція пересорту ${idx + 1}`}>
                    <div className="inventory-resort-row__header">
                      <strong>Позиція {idx + 1}</strong>
                      <button type="button" disabled={saving} className="inventory-resort-row__remove"
                        onClick={() => removeRow(idx)} title="Видалити позицію" aria-label={`Видалити позицію ${idx + 1}`}>×</button>
                    </div>
                    <div className="inventory-resort-row__pair">
                      <ItemQuantityField items={productSearchItems} value={r.fromProductId} onChange={id => updateRow(idx, { fromProductId: id })}
                        quantity={r.fromQuantity} onQuantityChange={fromQuantity => updateRow(idx, { fromQuantity })}
                        label="Списати (−)" ariaLabel={`Списати (−), позиція ${idx + 1}`} placeholder="Пошук продукту для списання…"
                        unit={getWeightTypeLabel(products.find(p => String(p.id) === r.fromProductId)?.weightType || '')} disabled={saving} />
                      <span className="inventory-resort-row__arrow" aria-hidden="true">→</span>
                      <ItemQuantityField items={productSearchItems} value={r.toProductId} onChange={id => updateRow(idx, { toProductId: id })}
                        quantity={r.toQuantity} onQuantityChange={toQuantity => updateRow(idx, { toQuantity })}
                        label="Оприбуткувати (+)" ariaLabel={`Оприбуткувати (+), позиція ${idx + 1}`} placeholder="Пошук продукту заміни…"
                        unit={getWeightTypeLabel(products.find(p => String(p.id) === r.toProductId)?.weightType || '')} disabled={saving} />
                    </div>
                    <div className="crm-position-insert">
                      <button className="crm-position-insert__button" type="button" disabled={saving} onClick={() => addRow(idx)} aria-label={`Додати позицію після ${idx + 1}`}>+ Додати нижче</button>
                    </div>
                  </section>
                ))}
              </div>
              {!rows.length && (
                <div className="crm-position-insert">
                  <button type="button" disabled={saving} onClick={() => addRow()} className="crm-position-insert__button">+ Додати позицію</button>
                </div>
              )}
              <div className="inventory-resort-form__actions">
                <button type="submit" disabled={saving || !rows.length} style={{ ...successButton, borderRadius: '999px' }}>{saving ? 'Збереження…' : 'Зберегти'}</button>
                <button type="button" disabled={saving} onClick={() => { setShowForm(false); resetForm(); }} style={{ ...neutralButton, borderRadius: '999px' }}>Скасувати</button>
              </div>
            </form>
          )}
          {selected && !showForm && (
            <div className="print-area" style={{ background: '#fff', padding: '16px', boxShadow: '0 1px 4px rgba(0,0,0,0.1)' }}>
              <div className="inventory-receipts-detail-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <h3>Пересорт #{selected.id} {selected.status === 'Cancelled' ? '· СКАСОВАНО' : selected.status === 'Draft' ? '· ЧЕРНЕТКА' : '· ПРОВЕДЕНО'}</h3>
                <div className="no-print inventory-receipts-detail-actions" style={{ display: 'flex', gap: '8px' }}>
                  {selected.status === 'Draft' && <button onClick={() => startEdit(selected)} style={{ ...btn, background: 'var(--crm-warning-bg)', color: 'var(--crm-warning)', borderColor: 'var(--crm-warning)' }}>Редагувати</button>}
                  {selected.status !== 'Cancelled' && <button onClick={() => remove(selected.id)} style={dangerButton}>Скасувати</button>}
                  <button onClick={print} style={neutralButton}>Друк</button>
                </div>
              </div>
              {selected.status !== 'Draft' && selected.status !== 'Cancelled' && <p style={{ color: 'var(--crm-text-muted)' }}>Проведений документ незмінний. Для виправлення скасуйте його та створіть новий.</p>}
              {selected.postedAtUtc && <p>Проведено: {new Date(selected.postedAtUtc).toLocaleString('uk-UA')} · Автор: {selected.postedByUsername || 'історичний запис'}</p>}
              <p>Дата: {new Date(selected.date).toLocaleDateString('uk-UA')}</p>
              {selected.notes && <p>Примітка: {selected.notes}</p>}
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr>
                    <th style={thStyle}>Назва продукту</th>
                    <th style={{ ...thStyle, textAlign: 'right' }}>Вага</th>
                    <th style={{ ...thStyle, textAlign: 'center' }}>Од.</th>
                    <th style={thStyle}>Назва продукту який замінили</th>
                    <th style={{ ...thStyle, textAlign: 'right' }}>Кінцева вага продукту заміни</th>
                    <th style={{ ...thStyle, textAlign: 'center' }}>Од.</th>
                  </tr>
                </thead>
                <tbody>
                  {selectedMetaRows.map((row, idx) => (
                    <tr key={`${row.fromProductId}-${row.toProductId}-${idx}`}>
                      <td style={tdStyle}>{productNameById.get(row.fromProductId) || '—'}</td>
                      <td style={{ ...tdStyle, textAlign: 'right' }}>{row.fromQuantity.toFixed(3)}</td>
                      <td style={{ ...tdStyle, textAlign: 'center' }}>{getWeightTypeLabel(products.find((p) => p.id === row.fromProductId)?.weightType || '')}</td>
                      <td style={tdStyle}>{productNameById.get(row.toProductId) || '—'}</td>
                      <td style={{ ...tdStyle, textAlign: 'right' }}>{row.toQuantity.toFixed(3)}</td>
                      <td style={{ ...tdStyle, textAlign: 'center' }}>{getWeightTypeLabel(products.find((p) => p.id === row.toProductId)?.weightType || '')}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default InventoryResortOpsPage;
